import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';

function CatDialog({ open, onClose, onSave, initial, title }) {
  const [form, setForm] = useState({ name: '', gl_account: '', description: '' });
  useEffect(() => { if (open) setForm(initial || { name: '', gl_account: '', description: '' }); }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm">
        <DialogHeader><DialogTitle>{initial ? 'Edit' : 'Add'} {title}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e=>set('name',e.target.value)} className="mt-1 h-8 text-sm" data-testid="category-name-input" /></div>
          <div><Label className="text-xs">GL Account *</Label><Input value={form.gl_account} onChange={e=>set('gl_account',e.target.value)} className="mt-1 h-8 text-sm font-mono" placeholder="e.g. 6050-001" data-testid="category-gl-input" /></div>
          <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={e=>set('description',e.target.value)} className="mt-1 h-8 text-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => onSave(form)} data-testid="category-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CategoriesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await API.get('/categories'); setItems(r.data); }
    catch(e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing) { await API.put(`/categories/${editing.id}`, form); toast.success('Updated'); }
      else { await API.post('/categories', form); toast.success('Created'); }
      setDialogOpen(false); setEditing(null); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const doDelete = async () => {
    try { await API.delete(`/categories/${deleteId}`); toast.success('Deleted'); setDeleteId(null); load(); }
    catch(e) { toast.error('Delete failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Categories</h1>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-category-btn">
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>
      <p className="text-sm text-slate-500">Map categories to Acumatica GL accounts for export.</p>
      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="categories-table">
          <thead><tr>
            <th>Category Name</th><th>GL Account</th><th>Description</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="text-center py-8 text-slate-400"><RefreshCw size={16} className="animate-spin inline" /></td></tr>
            ) : items.map(c => (
              <tr key={c.id}>
                <td className="font-medium">{c.name}</td>
                <td className="font-mono text-sm text-slate-600">{c.gl_account}</td>
                <td className="text-slate-500 text-sm">{c.description || '—'}</td>
                <td className="text-right">
                  <button onClick={() => { setEditing(c); setDialogOpen(true); }} className="p-1 text-slate-400 hover:text-emerald-600 mr-1"><Pencil size={13} /></button>
                  <button onClick={() => setDeleteId(c.id)} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CatDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }} onSave={save} initial={editing} title="Category" />
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Delete Category?</AlertDialogTitle><AlertDialogDescription>Existing allocations may lose their GL mapping.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
